import React, { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../utils/api";
import { useModal } from "../context/ModalContext";
import {
  Card,
  CardBody,
  PageHeader,
  Spinner,
  Badge,
  EmptyState,
} from "../components/ui";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaArrowLeft,
  FaProjectDiagram,
  FaHistory,
} from "react-icons/fa";
import FlowCanvas from "../components/flow/FlowCanvas";
import FlowHistory from "../components/flow/FlowHistory";
import GroupFollowUps from "../components/flow/GroupFollowUps";
import AITakeover from "../components/flow/AITakeover";

export default function FlowBuilder() {
  const modal = useModal();
  const [flows, setFlows] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFlow, setEditingFlow] = useState(null); // flow being edited on canvas
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Create flow form state
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSessionId, setNewSessionId] = useState("");

  const fetchFlows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("/api/autoflows");
      setFlows(res?.data || []);
    } catch {
      setFlows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await apiGet("/api/sessions");
      setSessions(
        (res?.data || []).filter((s) => s.status === "connected")
      );
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    fetchFlows();
    fetchSessions();
  }, [fetchFlows, fetchSessions]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newSessionId) return;

    try {
      const res = await apiPost("/api/autoflows", {
        name: newName.trim(),
        sessionId: newSessionId,
        nodes: [
          {
            id: "node-0",
            type: "trigger",
            position: { x: 250, y: 50 },
            data: { matchType: "contains", keyword: "" },
          },
        ],
        edges: [],
      });
      if (res?.success) {
        setNewName("");
        setNewSessionId("");
        setShowCreate(false);
        fetchFlows();
        // Open in editor immediately
        setEditingFlow(res.data);
      } else {
        modal.alert(res?.message || "Failed to create flow", "error");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to create flow", "error");
    }
  };

  const handleToggle = async (flow) => {
    const res = await apiPut(`/api/autoflows/${flow.id}/toggle`);
    if (res?.success) {
      fetchFlows();
    } else {
      modal.alert(res?.message || "Failed to toggle flow", "error");
    }
  };

  const handleDelete = async (flow) => {
    if (
      !(await modal.confirm(`Delete "${flow.name}"? This cannot be undone.`, {
        danger: true,
        confirmText: "Delete",
      }))
    )
      return;

    const res = await apiDelete(`/api/autoflows/${flow.id}`);
    if (res?.success) {
      fetchFlows();
    }
  };

  const handleEdit = async (flow) => {
    // Fetch full flow data
    const res = await apiGet(`/api/autoflows/${flow.id}`);
    if (res?.success) {
      setEditingFlow(res.data);
    }
  };

  const handleSaveCanvas = async (nodes, edges) => {
    if (!editingFlow) return;
    setSaving(true);
    try {
      const res = await apiPut(`/api/autoflows/${editingFlow.id}`, {
        nodes,
        edges,
      });
      if (res?.success) {
        modal.alert("Flow saved!", "success");
        setEditingFlow(res.data);
        fetchFlows();
      } else {
        modal.alert(res?.message || "Failed to save", "error");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  // ─── Canvas view ───
  if (editingFlow) {
    return (
      <div className="flex flex-col h-[calc(100vh-60px)]">
        {/* Canvas header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-surface-border bg-surface-card">
          <div className="flex items-center gap-3">
            <button
              className="btn-ghost btn-sm flex items-center gap-1.5"
              onClick={() => {
                setEditingFlow(null);
                fetchFlows();
              }}
            >
              <FaArrowLeft size={11} />
              Back
            </button>
            <div className="w-px h-5 bg-surface-border" />
            <h2 className="text-sm font-semibold text-ink-primary">
              {editingFlow.name}
            </h2>
            <Badge
              variant={editingFlow.active ? "success" : "neutral"}
            >
              {editingFlow.active ? "Active" : "Inactive"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`btn-sm flex items-center gap-1.5 ${showHistory ? "btn-primary" : "btn-ghost border border-surface-border"}`}
              onClick={() => setShowHistory(!showHistory)}
            >
              <FaHistory size={11} />
              History
            </button>
            <button
              className={`btn-sm flex items-center gap-1.5 ${
                editingFlow.active ? "btn-secondary" : "btn-primary"
              }`}
              onClick={async () => {
                const res = await apiPut(
                  `/api/autoflows/${editingFlow.id}/toggle`
                );
                if (res?.success) {
                  setEditingFlow(res.data);
                  fetchFlows();
                } else {
                  modal.alert(res?.message || "Failed to toggle", "error");
                }
              }}
            >
              {editingFlow.active ? (
                <>
                  <FaToggleOff size={12} /> Deactivate
                </>
              ) : (
                <>
                  <FaToggleOn size={12} /> Activate
                </>
              )}
            </button>
          </div>
        </div>

        {/* Canvas + History panel */}
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1">
            <FlowCanvas
              initialNodes={editingFlow.nodes || []}
              initialEdges={(editingFlow.edges || []).map((e) => ({
                ...e,
                animated: true,
                style: { stroke: "#00C897", strokeWidth: 2 },
              }))}
              onSave={handleSaveCanvas}
              saving={saving}
              sessions={sessions}
              selectedSessionId={editingFlow.sessionId}
            />
          </div>
          {showHistory && (
            <FlowHistory flowId={editingFlow.id} />
          )}
        </div>
      </div>
    );
  }

  // ─── List view ───
  return (
    <div className="page-content">
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="Flow Builder"
          subtitle="Automate responses based on incoming messages"
          guideUrl="/guide/flow-builder"
        />
        <button
          className="btn-primary flex items-center gap-1.5"
          onClick={() => setShowCreate(true)}
        >
          <FaPlus size={11} />
          New Flow
        </button>
      </div>

      {/* Create flow form */}
      {showCreate && (
        <Card className="mb-6 max-w-4xl">
          <CardBody>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: CTA + form */}
              <div>
                <form onSubmit={handleCreate}>
                  <h3 className="text-sm font-semibold text-ink-primary mb-1">
                    Create New Flow
                  </h3>
                  <p className="text-xs text-ink-muted mb-3">
                    Name your automation and pick the WhatsApp number it should run on.
                  </p>
                  <div className="space-y-3">
                    <div className="input-group">
                      <label className="input-label">Flow Name</label>
                      <input
                        className="input"
                        placeholder="e.g. Welcome Flow, Pricing Bot"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">WhatsApp Session</label>
                      <select
                        className="input"
                        value={newSessionId}
                        onChange={(e) => setNewSessionId(e.target.value)}
                        required
                      >
                        <option value="">Select a connected session</option>
                        {sessions.map((s) => (
                          <option key={s.sessionId} value={s.sessionId}>
                            {s.number || s.sessionId}
                          </option>
                        ))}
                      </select>
                      {sessions.length === 0 && (
                        <p className="input-hint text-amber-600">
                          No connected WhatsApp sessions found. Connect one first.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <button type="submit" className="btn-primary btn-sm">
                      Create your first flow
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => setShowCreate(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>

              {/* Right: What you can build */}
              <div className="lg:border-l lg:border-surface-border lg:pl-6">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-muted mb-3">
                  What you can build with Flow Builder
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2.5">
                    <FaProjectDiagram className="text-brand mt-0.5 flex-shrink-0" size={13} />
                    <div>
                      <div className="text-sm font-medium text-ink-primary">Auto-reply to keywords</div>
                      <div className="text-xs text-ink-muted">Send instant responses when customers message "price", "hours", or any keyword.</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <FaHistory className="text-brand mt-0.5 flex-shrink-0" size={13} />
                    <div>
                      <div className="text-sm font-medium text-ink-primary">Schedule follow-ups</div>
                      <div className="text-xs text-ink-muted">Drip messages over hours or days to nudge cold leads back into a conversation.</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <FaToggleOn className="text-brand mt-0.5 flex-shrink-0" size={13} />
                    <div>
                      <div className="text-sm font-medium text-ink-primary">AI-powered conversations</div>
                      <div className="text-xs text-ink-muted">Let an AI node answer free-form questions in your brand voice.</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <FaEdit className="text-brand mt-0.5 flex-shrink-0" size={13} />
                    <div>
                      <div className="text-sm font-medium text-ink-primary">Branch on customer replies</div>
                      <div className="text-xs text-ink-muted">Route conversations based on what they say — yes/no, menu picks, or intent.</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <FaPlus className="text-brand mt-0.5 flex-shrink-0" size={13} />
                    <div>
                      <div className="text-sm font-medium text-ink-primary">Trigger HTTP webhooks</div>
                      <div className="text-xs text-ink-muted">Sync to your CRM, post to Slack, or call any external API mid-flow.</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Flow list */}
      {loading ? (
        <div className="flex items-center gap-2 text-ink-muted py-8">
          <Spinner size="sm" /> Loading flows...
        </div>
      ) : flows.length === 0 ? (
        <EmptyState
          icon={FaProjectDiagram}
          title="No flows yet"
          description="Create your first automation flow to auto-respond to incoming messages."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {flows.map((flow) => (
            <Card
              key={flow.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleEdit(flow)}
            >
              <CardBody className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-ink-primary truncate">
                      {flow.name}
                    </h3>
                    <p className="text-xs text-ink-muted mt-0.5">
                      Session: {flow.sessionId}
                    </p>
                  </div>
                  <Badge variant={flow.active ? "success" : "neutral"}>
                    {flow.active ? "Active" : "Inactive"}
                  </Badge>
                </div>

                {/* Node count summary */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs text-ink-muted">
                    {(flow.nodes || []).length} nodes
                  </span>
                  <span className="text-xs text-ink-muted">
                    {(flow.edges || []).length} connections
                  </span>
                </div>

                {/* Actions */}
                <div
                  className="flex items-center gap-2 pt-3 border-t border-surface-border"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="btn-ghost btn-sm flex items-center gap-1"
                    onClick={() => handleEdit(flow)}
                  >
                    <FaEdit size={10} /> Edit
                  </button>
                  <button
                    className={`btn-sm flex items-center gap-1 ${
                      flow.active ? "btn-secondary" : "btn-primary"
                    }`}
                    onClick={() => handleToggle(flow)}
                  >
                    {flow.active ? (
                      <>
                        <FaToggleOff size={11} /> Deactivate
                      </>
                    ) : (
                      <>
                        <FaToggleOn size={11} /> Activate
                      </>
                    )}
                  </button>
                  <button
                    className="btn-danger btn-sm btn-icon ml-auto"
                    onClick={() => handleDelete(flow)}
                  >
                    <FaTrash size={10} />
                  </button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* AI Takeover — autonomous routines + replies, set up conversationally */}
      <AITakeover sessions={sessions} />

      {/* Group follow-ups — scheduled posts + reactive AI replies into groups */}
      <GroupFollowUps sessions={sessions} />
    </div>
  );
}
